import { useRef, useCallback } from 'react'
import { useTranslation } from 'next-i18next'
import { makeStyles, Container } from '@material-ui/core'
import T from '../../home-new/tokens'
import Reveal from '../../home-new/Reveal'

// "ฟีเจอร์ที่ช่วยให้จัดการงานส่งง่ายขึ้น" — bento of 5 text blocks interleaved
// with 4 wide feature illustrations, on a 3-column grid matching the design:
//   t1 i1 i1 / i2 t2 t3 / t4 i3 i3 / i4 i4 t5
// The wide (~3:1) illustrations 01/03/04 sit in 2-col slots so they show fully
// (no cover-crop); only 02 (i2) is a 1-col slot (subject centred, cover-safe).
// Reuses the dependency-free "Magic Bento" interaction from home-business
// BenefitsBiz (cursor spotlight, per-card border glow, tilt + magnetism, click
// ripple); prefers-reduced-motion → glows static, no tilt/ripple.

const useStyles = makeStyles((theme) => ({
  root: {
    padding: '70px 0',
    background: T.color.surface,
    overflow: 'hidden',
    [theme.breakpoints.down('sm')]: { padding: '44px 0' },
  },
  heading: {
    fontFamily: T.font.heading,
    fontSize: 32,
    fontWeight: 700,
    color: T.color.ink,
    textAlign: 'center',
    margin: '0 0 44px',
    '& span': { color: T.color.brand },
    [theme.breakpoints.down('sm')]: { fontSize: 22, margin: '0 0 28px' },
  },
  gridWrap: { position: 'relative', borderRadius: 24, '&:hover $spotlight': { opacity: 1 } },
  spotlight: {
    position: 'absolute',
    inset: '-40px',
    zIndex: 0,
    pointerEvents: 'none',
    opacity: 0,
    transition: 'opacity 0.45s ease',
    background:
      'radial-gradient(600px circle at var(--spot-x, 50%) var(--spot-y, 50%), rgba(0,158,255,0.16), rgba(0,158,255,0) 45%)',
    [theme.breakpoints.down('sm')]: { display: 'none' },
  },
  grid: {
    position: 'relative',
    zIndex: 1,
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gridTemplateAreas: `
      "t1 i1 i1"
      "i2 t2 t3"
      "t4 i3 i3"
      "i4 t5 t5"
    `,
    gap: '22px',
    [theme.breakpoints.down('sm')]: { display: 'flex', flexDirection: 'column', gap: '16px' },
  },
  card: {
    position: 'relative',
    isolation: 'isolate',
    overflow: 'hidden',
    willChange: 'transform',
    transition: 'transform 0.3s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.3s ease',
    '&::before': {
      content: '""',
      position: 'absolute',
      inset: 0,
      zIndex: -1,
      borderRadius: 'inherit',
      pointerEvents: 'none',
      opacity: 0,
      transition: 'opacity 0.4s ease',
      background:
        'radial-gradient(260px circle at var(--glow-x, 50%) var(--glow-y, 50%), rgba(0,158,255,0.12), transparent 60%)',
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      inset: 0,
      zIndex: -1,
      borderRadius: 'inherit',
      pointerEvents: 'none',
      padding: '1.5px',
      opacity: 0,
      transition: 'opacity 0.4s ease',
      background:
        'radial-gradient(320px circle at var(--glow-x, 50%) var(--glow-y, 50%), rgba(0,158,255,0.9), rgba(0,158,255,0.25) 55%, transparent 75%)',
      WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
      WebkitMaskComposite: 'xor',
      mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
      maskComposite: 'exclude',
    },
    '&:hover': { boxShadow: '0 16px 36px rgba(0,102,204,0.16)' },
    '&:hover::before, &:hover::after': { opacity: 1 },
    '@media (prefers-reduced-motion: reduce)': {
      transition: 'box-shadow 0.3s ease',
      '&:hover::before': { opacity: 0.5 },
      '&:hover::after': { opacity: 0.6 },
    },
  },
  textCard: {
    background: T.color.surface,
    borderRadius: 20,
    boxShadow: T.shadow.card,
    padding: '32px 30px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    [theme.breakpoints.down('sm')]: { padding: '24px 22px' },
  },
  imgCard: {
    background: T.color.canvasAlt,
    borderRadius: 20,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 210,
    '& img': { width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center' },
    [theme.breakpoints.down('sm')]: { minHeight: 'auto' },
  },
  cardTitle: {
    fontFamily: T.font.heading,
    fontSize: 22,
    fontWeight: 600,
    color: T.color.brand,
    margin: '0 0 12px',
    [theme.breakpoints.down('sm')]: { fontSize: 18 },
  },
  cardDesc: {
    fontFamily: T.font.body,
    fontSize: 15,
    color: T.color.muted,
    lineHeight: 1.7,
    margin: 0,
    [theme.breakpoints.down('sm')]: { fontSize: 14 },
  },
  a_t1: { gridArea: 't1' },
  a_t2: { gridArea: 't2' },
  a_t3: { gridArea: 't3' },
  a_t4: { gridArea: 't4' },
  a_t5: { gridArea: 't5' },
  a_i1: { gridArea: 'i1' },
  a_i2: { gridArea: 'i2' },
  a_i3: { gridArea: 'i3' },
  a_i4: { gridArea: 'i4' },
}))

const prefersReduced = () =>
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches

const useMagicBento = () => {
  const raf = useRef(null)

  const onGridMove = useCallback((e) => {
    if (prefersReduced()) return
    const el = e.currentTarget
    const r = el.getBoundingClientRect()
    el.style.setProperty('--spot-x', `${e.clientX - r.left}px`)
    el.style.setProperty('--spot-y', `${e.clientY - r.top}px`)
  }, [])

  const onCardMove = useCallback((e) => {
    if (prefersReduced()) return
    const el = e.currentTarget
    const r = el.getBoundingClientRect()
    if (!r.width || !r.height) return
    const x = e.clientX - r.left
    const y = e.clientY - r.top
    const px = (x / r.width) * 100
    const py = (y / r.height) * 100
    const rotateY = ((x / r.width) - 0.5) * 8
    const rotateX = -((y / r.height) - 0.5) * 8
    const magX = ((x / r.width) - 0.5) * 6
    const magY = ((y / r.height) - 0.5) * 6
    if (raf.current) cancelAnimationFrame(raf.current)
    raf.current = requestAnimationFrame(() => {
      el.style.setProperty('--glow-x', `${px}%`)
      el.style.setProperty('--glow-y', `${py}%`)
      el.style.transform = `perspective(800px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) translate(${magX}px, ${magY - 5}px) scale(1.012)`
    })
  }, [])

  const onCardLeave = useCallback((e) => {
    const el = e.currentTarget
    if (raf.current) cancelAnimationFrame(raf.current)
    raf.current = requestAnimationFrame(() => {
      el.style.transform = ''
      el.style.removeProperty('--glow-x')
      el.style.removeProperty('--glow-y')
    })
  }, [])

  const onCardDown = useCallback((e) => {
    if (prefersReduced() || typeof document === 'undefined') return
    const el = e.currentTarget
    const r = el.getBoundingClientRect()
    const size = Math.max(r.width, r.height) * 1.1
    const span = document.createElement('span')
    span.style.cssText = [
      'position:absolute',
      `left:${e.clientX - r.left}px`,
      `top:${e.clientY - r.top}px`,
      `width:${size}px`,
      `height:${size}px`,
      `margin-left:${-size / 2}px`,
      `margin-top:${-size / 2}px`,
      'border-radius:50%',
      'pointer-events:none',
      'z-index:-1',
      'background:radial-gradient(circle, rgba(0,158,255,0.32), rgba(0,158,255,0) 70%)',
    ].join(';')
    el.appendChild(span)
    if (typeof span.animate === 'function') {
      const anim = span.animate(
        [
          { transform: 'scale(0)', opacity: 0.6 },
          { transform: 'scale(1)', opacity: 0 },
        ],
        { duration: 600, easing: 'ease-out' }
      )
      anim.onfinish = () => span.remove()
    } else {
      setTimeout(() => span.remove(), 600)
    }
  }, [])

  return { onGridMove, onCardMove, onCardLeave, onCardDown }
}

const ServiceFeatures = () => {
  const classes = useStyles()
  const { t } = useTranslation('services')
  const mb = useMagicBento()
  const cardHandlers = {
    onMouseMove: mb.onCardMove,
    onMouseLeave: mb.onCardLeave,
    onMouseDown: mb.onCardDown,
  }

  const texts = {
    t1: { title: t('new.features.t1.title'), desc: t('new.features.t1.desc') },
    t2: { title: t('new.features.t2.title'), desc: t('new.features.t2.desc') },
    t3: { title: t('new.features.t3.title'), desc: t('new.features.t3.desc') },
    t4: { title: t('new.features.t4.title'), desc: t('new.features.t4.desc') },
    t5: { title: t('new.features.t5.title'), desc: t('new.features.t5.desc') },
  }

  const TextCard = ({ area, data }) => (
    <div className={`${classes.card} ${classes.textCard} ${area}`} {...cardHandlers}>
      <h3 className={classes.cardTitle}>{data.title}</h3>
      <p className={classes.cardDesc}>{data.desc}</p>
    </div>
  )

  // webp sibling was generated next to each .png (sized down); the card CSS
  // (objectFit:cover, 100%×100%) drives display, so webp is served to
  // supporting browsers with the PNG as fallback. Below the fold → lazy.
  const ImgCard = ({ area, src, alt }) => (
    <div className={`${classes.card} ${classes.imgCard} ${area}`} {...cardHandlers}>
      <picture style={{ display: 'contents' }}>
        <source srcSet={src.replace(/\.png$/, '.webp')} type="image/webp" />
        <img src={src} alt={alt} loading="lazy" decoding="async" />
      </picture>
    </div>
  )

  return (
    <section className={classes.root} id="features">
      <Container maxWidth={false} style={{ maxWidth: T.container }}>
        <Reveal>
          <h2 className={classes.heading}>
            {t('new.features.heading-lead')}
            <span>{t('new.features.heading-highlight')}</span>
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <div className={classes.gridWrap} onMouseMove={mb.onGridMove}>
            <span className={classes.spotlight} aria-hidden="true" />
            <div className={classes.grid}>
              <TextCard area={classes.a_t1} data={texts.t1} />
              <ImgCard area={classes.a_i1} src="/static/img/service-new/v2/features/01.png" alt={texts.t1.title} />
              <ImgCard area={classes.a_i2} src="/static/img/service-new/v2/features/03.png" alt={texts.t2.title} />
              <TextCard area={classes.a_t2} data={texts.t2} />
              <TextCard area={classes.a_t3} data={texts.t3} />
              <TextCard area={classes.a_t4} data={texts.t4} />
              <ImgCard area={classes.a_i3} src="/static/img/service-new/v2/features/02.png" alt={texts.t4.title} />
              <ImgCard area={classes.a_i4} src="/static/img/service-new/v2/features/04.png" alt={texts.t5.title} />
              <TextCard area={classes.a_t5} data={texts.t5} />
            </div>
          </div>
        </Reveal>
      </Container>
    </section>
  )
}

export default ServiceFeatures
